import React, { useState } from 'react';
import { Button, Card, Col, Modal, Row, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import posthog, {
  POSTHOG_GROUP_TYPES,
  setPosthogGroups,
} from 'src/services/posthog';
import {
  captureExamEvent,
  EXAM_EVENTS,
  markExamStarted,
} from 'src/services/posthogEvents';
import { APP_EVENTS, captureAppEvent } from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { I18NextNS } from 'src/models/Language.model';
import { PatientInfoKeyMapOptain, Session } from 'src/models/Session.model';

import useNavigateAfterConfirm from 'src/hooks/useNavigateAfterConfirm';
import useResumeExam from 'src/hooks/useResumeExam';

type TeleophthPatientConfirmationModalProps = {
  formValues: Partial<Session>;
  isShowConfirmModal: boolean;
  handleCloseConfirmModal: () => void;
};

export default function TeleophthPatientConfirmationModal({
  formValues,
  isShowConfirmModal,
  handleCloseConfirmModal,
}: TeleophthPatientConfirmationModalProps) {
  const { t } = useTranslation(I18NextNS.COMMON);
  const { updateSession } = useRemoteBoundStore();
  const { initializeResumedExam } = useResumeExam();
  const navigateAfterConfirm = useNavigateAfterConfirm();
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    updateSession(formValues);

    setIsConfirming(true);
    const resolvedSiteId = await initializeResumedExam(
      formValues.orderId!,
      formValues.siteId,
    );

    posthog.group(POSTHOG_GROUP_TYPES.ORDER, formValues.orderId!);
    if (resolvedSiteId) {
      setPosthogGroups({ [POSTHOG_GROUP_TYPES.SITE]: resolvedSiteId });
    }
    markExamStarted();
    captureExamEvent(EXAM_EVENTS.EXAM_RESUMED, {
      order_id: formValues.orderId!,
    });
    setIsConfirming(false);
    captureAppEvent(APP_EVENTS.PATIENT_INFORMATION_CONFIRMED);
    navigateAfterConfirm();
  };

  return (
    <CustomModal
      show={isShowConfirmModal}
      onHide={handleCloseConfirmModal}
      scrollable={true}
      fullscreen="xl-down"
    >
      <Modal.Header closeButton>
        <Modal.Title data-test="confirm-patient-info-modal-title">
          {t('patientFields.confirmPatientInformation')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card>
          <Card.Body>
            <Stack gap={2}>
              {(
                ['id', 'firstName', 'lastName', 'dob'] as Array<
                  keyof Omit<Session, 'template'>
                >
              ).map((objKey) => (
                <Row key={objKey}>
                  <Col>
                    <span className="fw-medium">
                      {t(
                        `patientFields.${PatientInfoKeyMapOptain[objKey as keyof typeof PatientInfoKeyMapOptain]}`,
                      )}
                    </span>
                  </Col>
                  <Col>
                    <span
                      data-test={`patient-info-${objKey.replace(/([A-Z])/g, '-$1').toLowerCase()}`}
                    >
                      {formValues[objKey]?.toString()}
                    </span>
                  </Col>
                </Row>
              ))}
            </Stack>
          </Card.Body>
        </Card>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary-100"
          onClick={handleCloseConfirmModal}
          disabled={isConfirming}
          data-track="exam.patient-confirmation-modal.cancel"
          data-test="confirm-patient-cancel"
          data-observe
        >
          {t('modal.cancel')}
        </Button>

        <Button
          variant="primary"
          className="shadow"
          onClick={handleConfirm}
          disabled={isConfirming}
          data-track="exam.patient-confirmation-modal.confirm"
          data-test="confirm-patient-information"
          data-observe
        >
          {isConfirming ? t('modal.confirming') : t('modal.confirm')}
        </Button>
      </Modal.Footer>
    </CustomModal>
  );
}
